import React, { SyntheticEvent, useCallback } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useUser } from '@auth0/nextjs-auth0/client';
import { get, cloneDeep } from 'lodash';
import { Drawer, ListItem, ListItemText, List, Divider } from '@material-ui/core';
// import SearchIcon from '@material-ui/icons/Search';
import Qpopper from '../QPopper';
import DropDownMenu from './DropDownNav';
import {
  featureFlagNavKeyMapping,
  avoidSortNavigationUniqueId,
  SidebarNav,
  sideBarNavs,
  HelpCenterContentAI,
  HomeNavForContentAI,
  selectionToggleForEnterpriseUser,
  aiToolsNavConfig,
  contentAiNavConfig,
  sidebarDefaultAiSeoNavs,
  sidebarConfigAiToolsSpecificUsers,
} from '../../constants/navigationMeta';
import { isProductionEnv, processSubscriptionData, USER_CLASSIFICATION } from '../../utility/common';
import { Backdrop } from '@material-ui/core';
import { useSelector } from 'react-redux';
import { useIsTrial } from '../../utility/services/customHooks';
import { signOut } from '../../utility/services/authActions';
import { useIsSubscriberUser, useSchema, useTestDriveDomain } from '../../utility/services/customHooks';
import {
  useIsContentAIUser,
  useIsInternalUser,
  useIsTestDriveUser,
  useSession,
} from '../../utility/services/customHooks/auth';
import { SUBSCRIPTION_STATUS } from '../../constants/common';
import { isSideNavItemActive, personalFolderIdsFromState } from '../../utility/adhocSideNavActive';
import CreditsComponent from './CreditsComponent';
import PlanDetails from './PlanDetails';

type AnchorData = {
  ele?: Element | null;
  to?: string;
};

export default function Sidebar(props) {
  const [anchorData, setAnchorData] = React.useState<AnchorData>({});
  const [hoverTimeout, setHoverTimeout] = React.useState<NodeJS.Timeout | null>(null);
  const schemaName = useSchema();
  const { session } = useSession();
  const customerInfo = useSelector((state) => state.root.customerInfo || []);
  const showSubscriberBanner = useSelector((state) => state.root.showSubscriberBanner);
  const personalFolders = useSelector((state) => state.root.personalFolders || []);
  const personalFolderId = useSelector((state) => state.root.personalFolderId);
  const personalFolderIds = personalFolderIdsFromState(personalFolders, personalFolderId);
  const customerData = customerInfo.find((o) => o.cust_code === schemaName);
  const router = useRouter();
  const { asPath } = router;
  const pathname = asPath;
  let sideBarNavOptions = cloneDeep([...sidebarDefaultAiSeoNavs]);
  let defaultSideBarNavOptions = cloneDeep([...sidebarDefaultAiSeoNavs]);
  let defaultSideBarForAiSeoUsers = cloneDeep([...sideBarNavs]);
  let aiToolsNav = cloneDeep([...aiToolsNavConfig]);
  let contentAiNav = cloneDeep([...contentAiNavConfig]);
  let sidebarConfigAiToolsSpecificUser = cloneDeep(sidebarConfigAiToolsSpecificUsers);

  const testDriveDomain = useTestDriveDomain();
  const fullYear = new Date()?.getFullYear();
  const [subscriberMeta, setSubscriberMeta] = React.useState<any>({});
  const SubscriptionStatus = get(session, 'user.subscription_status');
  const ToolsSubscription = get(session, 'user.qwa_tools_subscription_status');

  // default to "least" privileged options for information about the user
  // TODO we should "add" menu options rather than "remove" menu options
  // const isSubscriberUser = useIsSubscriberUser() ?? true;
  // const isInternalUser = useIsInternalUser() ?? false;
  // const isTestDriveUser = useIsTestDriveUser() ?? true;
  // const isTrial = useIsTrial() ?? true;

  // Avoid defaults due to long load times for now
  // TODO when the session is moved into redux or we migrate away from our solution
  // we can delete this code and uncomment the above
  const isSubscriberUser = useIsSubscriberUser();
  const isInternalUser = useIsInternalUser();
  // CI3 access for the LEFT SIDEBAR: only @quattr.com users or those holding a
  // CONTENT_INDEX_ADMIN/OPERATOR/VIEWER Auth0 role may see the Content Index
  // link. This mirrors the in-page access gate (ContentIndexV3 `ci3RoleAssigned`)
  // so an EXTERNAL user with no CI3 role doesn't even see the menu entry.
  // `authUser` (the raw Auth0 user with the `qwa-roles` claim) loads async, so we
  // only enforce the hide once it has resolved — until then we leave the
  // schema-configured visibility untouched to avoid flicker for legit users.
  const { user: authUser } = useUser();
  const ci3RoleAssigned = React.useMemo(() => {
    if (!authUser) return null; // not resolved yet
    const email = String((authUser as any)?.email || '');
    if (email.toLowerCase().endsWith('@quattr.com')) return true;
    const roles = (((authUser as any)?.['qwa-roles'] as string[] | undefined) || []).map((r) =>
      String(r).toUpperCase()
    );
    return ['CONTENT_INDEX_ADMIN', 'CONTENT_INDEX_OPERATOR', 'CONTENT_INDEX_VIEWER'].some((r) => roles.includes(r));
  }, [authUser]);
  const isTestDriveUser = useIsTestDriveUser();
  const IsContentAIUser = useIsContentAIUser();
  const isTrial = useIsTrial();

  const isCancelled =
    (SubscriptionStatus &&
      (SubscriptionStatus === SUBSCRIPTION_STATUS.SUBSCRIPTION_CANCELLED ||
        SubscriptionStatus === SUBSCRIPTION_STATUS.SUBSCRIPTION_PAUSED)) ||
    subscriberMeta.isSubscriptionCancelled;

  const isActiveToolsSubscription =
    ToolsSubscription &&
    [
      SUBSCRIPTION_STATUS.SUBSCRIPTION_ACTIVATED,
      SUBSCRIPTION_STATUS.SUBSCRIPTION_RESUMED,
      SUBSCRIPTION_STATUS.SUBSCRIPTION_REACTIVATED,
      SUBSCRIPTION_STATUS.SUBSCRIPTION_CREATED,
    ].includes(ToolsSubscription);
  // default to desktop when doing server side rendering/build time
  const isMobile = props.isMobile ?? false;
  const toggleDrawer = props.toggleDrawer;

  const { open, userType, defaultUser } = props;
  const role = get(customerData, 'user_role');
  const sideBarConfig = get(customerData, 'sidebar_link_config');
  const isDemoUser = customerData?.additional_user_info?.isDemoUser;

  const isMenuLinkVisible = useCallback(
    (key) => {
      if (defaultUser === 'CONTENT_AI' && isCancelled && !isActiveToolsSubscription) {
        if (key === 'user-type-selection') {
          return true;
        } else {
          return false;
        }
      }
      if (defaultUser === 'CONTENT_AI' && isActiveToolsSubscription && isCancelled) {
        if (userType === 'CONTENT_AI') {
          if (key === 'user-type-selection') {
            return true;
          } else {
            return false;
          }
        } else if (userType === 'QWA_TOOLS') {
          return true;
        }
      }

      if (
        defaultUser === 'QWA_TOOLS' &&
        !['user-type-selection'].includes(key) &&
        ['CONTENT_AI', 'AI_SEO_SUITE'].includes(userType)
      ) {
        return false;
      }
      if ((isDemoUser !== undefined && isDemoUser) || key === 'home') {
        return true;
      }
      if (userType !== undefined && userType == 'QWA_TOOLS') {
        return true;
      }
      return sideBarConfig?.[key]; // Default to false if the key is not present in the configuration
    },
    [isDemoUser, sideBarConfig, isCancelled, userType, defaultUser] // Add the necessary dependencies here
  );

  React.useEffect(() => {
    if (customerData && customerData?.SubscriberUserDetails) {
      const _subscriberMeta = processSubscriptionData(customerData);
      setSubscriberMeta(_subscriberMeta);
    }
  }, [customerData]);

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
      }
    };
  }, [hoverTimeout]);

  React.useEffect(() => {
    // to close drawer on path change
    if (isMobile) {
      toggleDrawer(false);
    }
    if (isInternalUser) {
      if (sideBarConfig) {
        if (sideBarConfig['il-autonomous'] === undefined) {
          sideBarConfig['il-autonomous'] = true;
        } else {
          sideBarConfig['il-autonomous'] = true;
        }
      }
    }
    // Content Index lock-state. Schema owns whether the feature is enabled via
    // sidebar_link_config['content-index']. Coerce to an explicit boolean so
    // DropDownNav's lock pattern fires (it requires isDisabledMenu !== undefined).
    // Truthy check (not === true) - Snowflake JSON can deliver this as boolean
    // true, string "true", or number 1; isMenuLinkVisible reads it truthy too.
    // 'content-index-main' is the legacy subnav key, still honored for any
    // schema that has it set so an in-flight rollout doesn't regress.
    if (sideBarConfig) {
      const ciEnabled = Boolean(sideBarConfig['content-index']) || Boolean(sideBarConfig['content-index-main']);
      sideBarConfig['content-index'] = ciEnabled;
    }
  }, [isMobile, pathname, toggleDrawer, isInternalUser, sideBarConfig]);

  if (!get(customerData, 'additional_user_info')) {
    sideBarNavOptions = sideBarNavOptions.filter((i) => !i.isDynamicNav);
    // keeping all static navs
  } else {
    const allowedDynamicNavs = Object.keys(featureFlagNavKeyMapping).map((i) => {
      if (get(customerData, `additional_user_info.${i}`)) {
        return featureFlagNavKeyMapping[i];
      } else {
        if (i === 'intentmanagement') {
          if (get(customerData, `additional_user_info.${i}`, true) !== false) {
            return featureFlagNavKeyMapping[i];
          }
        }
      }
      return 'N/A';
    });

    if (!isInternalUser) {
      sideBarNavOptions = sideBarNavOptions.filter(
        (i) => (i.isDynamicNav && allowedDynamicNavs.includes(i.key)) || !i.isDynamicNav || i.isInternal
      );
    }
    if (isCancelled && !isActiveToolsSubscription && !pathname.includes('/app/content-ai-home')) {
      router.push('/app/content-ai-home');
    }
  }

  if (isSubscriberUser && (!customerData || testDriveDomain || role === undefined || role === 'EXTERNAL')) {
    sideBarNavOptions = sideBarNavOptions.filter((i) => {
      let flag = false;
      if (i?.isSubscriberUserShow) {
        if (i.key === 'trends' && i.subNavs) {
          i.subNavs = i.subNavs.filter((subnav) => !subnav.title.includes('Trends Watchlist'));
        }
        flag = true;
      }
      return flag;
    });
    if (isSubscriberUser && customerInfo.length === 1 && !testDriveDomain) {
      // if external user clears his localstorage he gets access to all subscribers contents. this is to avoid that.
      signOut();
    }
    if (customerData && !sideBarNavOptions.some((i) => i.key !== undefined && pathname.includes(i.key))) {
      router.push('/404');
    }
  }

  if (isTrial) {
    // extract content-ai sub section
    let contentAiStart = -1,
      contentAiEnd = -1;
    for (let sideBarIdx = 0; sideBarIdx < sideBarNavOptions.length; sideBarIdx++) {
      const currentNav = sideBarNavOptions[sideBarIdx];

      const startFound = contentAiStart !== -1;
      if (!startFound) {
        if (currentNav.isSection && currentNav.key === 'content-ai') {
          contentAiStart = sideBarIdx;
        }
      } else {
        const isEndOfSection = currentNav.isSection && currentNav.key !== 'content-ai';
        if (isEndOfSection) {
          contentAiEnd = sideBarIdx;

          // we found the start and end
          break;
        }
      }
    }

    /**
     * @type {import('../../constants/navigationMeta').SidebarNav[]}
     */
    let contentAiSubSection = [] as SidebarNav[];
    if (contentAiStart !== -1 && contentAiEnd !== -1) {
      contentAiSubSection = sideBarNavOptions.slice(contentAiStart, contentAiEnd);
    }

    // only keep content-ai options
    sideBarNavOptions = contentAiSubSection;
  }
  const setAnchor = (event: SyntheticEvent<Element, MouseEvent> | null, to?: any) => {
    setAnchorData({ ele: event?.currentTarget as Element | null, to });
  };

  const hoveredSubnavs = sideBarNavOptions.find((i) => anchorData?.to === i.key);
  if (sideBarConfig !== undefined) {
    const isAnlyticEnabled = avoidSortNavigationUniqueId?.some((key) => sideBarConfig[key]);
    //sort sidebar menus based on the activated tabs true in db config, taking sequentially
    if (!isAnlyticEnabled) {
      sideBarNavOptions.sort((a, b) => {
        // Find the uniqueKey values of a and b within the sideBarConfig object
        if (sideBarConfig !== undefined) {
          const uniqueKeyA = a.uniqueKey as keyof typeof sideBarConfig;
          const uniqueKeyB = b.uniqueKey as keyof typeof sideBarConfig;
          // Check if a and b exist in the sideBarConfig object and their values are true
          const hasTrueA = sideBarConfig[uniqueKeyA] === true;
          const hasTrueB = sideBarConfig[uniqueKeyB] === true;
          if (hasTrueA && !hasTrueB) {
            return -1; // a comes before b
          } else if (!hasTrueA && hasTrueB) {
            return 1; // b comes before a
          }
        }
        // Maintain the original order for items with the same condition or if the condition is not met
        return 0;
      });
      //Need in future if sort specific menus only
      // sideBarNavOptions.sort((a, b) => {
      //   const indexA = sideBarSectionOrder?.indexOf(a.uniqueKey ?? '');
      //   const indexB = sideBarSectionOrder?.indexOf(b.uniqueKey ?? '');
      //   if (indexA > -1 && indexB > -1) {
      //     return indexA - indexB; // Sort based on the position in the sideBarSectionOrder array
      //   } else if (indexA > -1) {
      //     return -1; // a comes before b
      //   } else if (indexB > -1) {
      //     return 1; // b comes before a
      //   }
      //   return 0; // maintain original order
      // });
    }
  }

  if (
    get(customerData, 'additional_user_info') &&
    !isDemoUser &&
    (IsContentAIUser || isMenuLinkVisible('is-content-enabled-only'))
  ) {
    sideBarNavOptions.splice(1, 0, HomeNavForContentAI);
    // sideBarNavOptions.splice(6, 0, HelpCenterContentAI);
  }

  //setting sidebar menu for ai_tools section
  if ([userType].includes('QWA_TOOLS')) {
    sideBarNavOptions = aiToolsNav;
  }
  //setting sidebar menu for content_ai section
  if ([userType].includes('CONTENT_AI')) {
    sideBarNavOptions = contentAiNav;
  }
  //setting sidebar menu for ai_seo_suite section
  if ([userType].includes('AI_SEO_SUITE') && defaultUser !== undefined) {
    sideBarNavOptions = defaultSideBarNavOptions;
  }
  //setting sidebar menu for ai_seo_suite user selecting content_ai
  if (defaultUser == undefined && ![userType].includes('QWA_TOOLS')) {
    sideBarNavOptions = defaultSideBarForAiSeoUsers;
  }

  // Trends for Content AI customers — config-driven, per-schema opt-in.
  // Trends is intentionally absent from contentAiNavConfig. When this schema has
  // the parent flag sidebar_link_config['trends'] enabled, inject Trends into the
  // Content AI nav so the customer can reach it (e.g. AI Visibility) without
  // having to switch to the AI SEO Suite view. It is surfaced under its own
  // "Answer Engine Insights" section above the Content AI section (Content AI
  // tenant naming). Only the subnavs explicitly enabled in sidebar_link_config
  // are shown — locked upsell items are omitted for the Content AI view.
  // sideBarConfig is derived from customerData matched by cust_code === schemaName
  // (strictly per-schema), so other Content AI customers are unaffected. cloneDeep
  // is required because the subscriber filter above mutates subNavs in place.
  if ([userType].includes('CONTENT_AI') && sideBarConfig?.['trends']) {
    const trendsNav = sidebarDefaultAiSeoNavs.find((nav) => nav.uniqueKey === 'trends');
    if (trendsNav && !sideBarNavOptions.some((nav) => nav.uniqueKey === 'trends')) {
      const enabledTrends = cloneDeep(trendsNav);
      enabledTrends.subNavs = (enabledTrends.subNavs || []).filter(
        (sub) => sub.uniqueKey && sideBarConfig[sub.uniqueKey]
      );
      if (enabledTrends.subNavs.length > 0) {
        const answerEngineSection: SidebarNav = {
          title: 'Answer Engine Insights',
          isDynamicNav: false,
          isSection: true,
          isSubscriberUserShow: true,
          key: 'answer-engine-insights',
          uniqueKey: 'answer-engine-insights',
          subNavs: [],
        };
        // surface above the Content AI section, right after Home
        const homeIdx = sideBarNavOptions.findIndex((nav) => nav.uniqueKey === 'home');
        const insertAt = homeIdx === -1 ? 0 : homeIdx + 1;
        sideBarNavOptions.splice(insertAt, 0, answerEngineSection, enabledTrends);
      }
    }
  }

  //adding sidebar selector for ai_seo_suite user specific

  if (props?.isEnterpriseUser && !isMobile) {
    if (!sideBarNavOptions.includes(selectionToggleForEnterpriseUser) && defaultUser == undefined) {
      selectionToggleForEnterpriseUser.isAtBottom = true;
      sideBarNavOptions.splice(sideBarNavOptions.length, 0, selectionToggleForEnterpriseUser);
    } else if (!sideBarNavOptions.includes(selectionToggleForEnterpriseUser) && defaultUser !== undefined) {
      selectionToggleForEnterpriseUser.isAtBottom = false;
      sideBarNavOptions.splice(0, 0, selectionToggleForEnterpriseUser);
    }
  }
  //adding sidebar selector for ai_seo_suite user
  // if (props?.isEnterpriseUser && !isMobile) {
  //   if (!sideBarNavOptions.includes(selectionToggleForEnterpriseUser)) {
  //     selectionToggleForEnterpriseUser.isAtBottom = true;
  //     sideBarNavOptions.splice(sideBarNavOptions.length, 0, selectionToggleForEnterpriseUser);
  //   }
  // }
  //adding sidebar selector for mobile user
  if (isMobile) {
    if (!sideBarNavOptions.includes(selectionToggleForEnterpriseUser)) {
      selectionToggleForEnterpriseUser.isAtBottom = false;
      sideBarNavOptions.splice(0, 0, selectionToggleForEnterpriseUser);
    }
  }

  if (!isInternalUser) {
    sideBarNavOptions = sideBarNavOptions
      .filter((i) => {
        if (i.isInternal) {
          return false;
        } else if (i.key !== 'settings') {
          return true;
        } else if (i.subNavs) {
          const isSettingEnbaledCI = isMenuLinkVisible('is-content-enabled-only');
          if (!isSettingEnbaledCI) {
            // Check if any subnav has display: true, if so, show the parent Settings nav
            const hasVisibleSubNav = i.subNavs.some((subNav) => subNav.display === true);
            return i.display === true || hasVisibleSubNav;
          }
          return true;
        }
      })
      .map((i) => {
        // Filter subNavs to only show items with display: true for Settings
        if (i.key === 'settings' && i.subNavs) {
          const isSettingEnbaledCI = isMenuLinkVisible('is-content-enabled-only');
          if (!isSettingEnbaledCI) {
            return {
              ...i,
              subNavs: i.subNavs.filter((subNav) => subNav.display === true),
            };
          }
        }
        return i;
      });
    const intentFlag = get(customerData, 'additional_user_info', false);
    if (intentFlag) {
      const getintentFlag = get(intentFlag, 'intentmanagement', true);
      if (!getintentFlag && pathname.includes('/app/settings/')) {
        router.push('/404');
      }
    }
  }

  //[HOT-FIX]-Enable-multidomain-support-to-enterprise-customers
  // if (!isInternalUser) {
  //   sideBarNavOptions = sideBarNavOptions?.map((nav) => {
  //     if (nav.subNavs && !isMenuLinkVisible('is-content-enabled-only')) {
  //       return {
  //         ...nav,
  //         subNavs: nav.subNavs.filter((subNav) => subNav.uniqueKey !== 'settings-setup'),
  //       };
  //     }
  //     return nav;
  //   });
  // }
  if (![userType].includes('QWA_TOOLS')) {
    sideBarNavOptions.push(HelpCenterContentAI);
  }

  // Apply after userType/defaultUser nav swaps (those reset sideBarNavOptions from unfiltered clones).
  if (get(customerData, 'additional_user_info.hideAdhocAnalysisNav')) {
    sideBarNavOptions = sideBarNavOptions.filter((i) => i.key !== 'adhoc-analysis');
    if (pathname.includes('/app/adhoc-analysis')) {
      router.push('/404');
    } else if (!sideBarNavOptions.some((i) => i.key !== undefined && pathname.includes(i.key)) && !isDemoUser) {
      router.push('/404');
    }
  }

  // Content Index: REMOVE the nav entry entirely for an EXTERNAL user with no
  // CI3 role (ci3RoleAssigned === false). Setting sidebar_link_config falsy only
  // renders it as a *locked* upsell (lock icon) — we want it gone, not locked.
  // `null` = role not resolved yet, so leave it; @quattr.com / role-holders
  // resolve to true and keep the normal (enabled or schema-locked) entry. If the
  // user is sitting on /app/content-index when this resolves, bounce to /404.
  if (ci3RoleAssigned === false) {
    sideBarNavOptions = sideBarNavOptions.filter((i) => i.uniqueKey !== 'content-index' && i.key !== 'content-index');
    if (pathname.includes('/app/content-index')) {
      router.push('/404');
    }
  }

  const getSideBarComponent = useCallback(() => {
    if (isTestDriveUser !== undefined) {
      return (
        sideBarNavOptions
          //.filter((nav) => isValid || isMenuLinkVisible(nav.uniqueKey))
          .map((nav) => (
            <>
              {!nav.isAtBottom ? (
                <div className={nav?.selectBox ? 'sidebar-select-box' : ''}>
                  <DropDownMenu
                    id={`layout__menu__side_bar__${nav.key}`}
                    key={nav.key}
                    title={nav.title}
                    name={nav.key}
                    // setAnchor={setAnchor}
                    setAnchor={() => {}}
                    pathname={pathname}
                    icon={nav.icon}
                    subNavs={nav.subNavs}
                    isSection={nav.isSection}
                    sideBarConfig={
                      defaultUser !== USER_CLASSIFICATION.QWA_TOOLS ? sideBarConfig : sidebarConfigAiToolsSpecificUser
                    }
                    isDisabledMenu={isMenuLinkVisible(nav.uniqueKey)}
                    isDemoUser={isDemoUser}
                    isDrawerOpen={open}
                    to={nav.to}
                    toExternal={nav.toExternal}
                    IsPlaneNav={nav.IsPlaneNav}
                    keepDefaultExpanded={
                      userType !== USER_CLASSIFICATION.AI_SEO_SUITE &&
                      (IsContentAIUser || isMenuLinkVisible('is-content-enabled-only'))
                    }
                    isHelpCenter={nav.key === 'helpcenter'}
                    customerName={
                      customerData
                        ? customerData?.cust_name
                        : defaultUser === USER_CLASSIFICATION.QWA_TOOLS
                        ? USER_CLASSIFICATION.QWA_TOOLS
                        : null
                    }
                    selectBox={nav?.selectBox}
                    filterChange={props?.handleUserToggle}
                    options={props?.options}
                    userType={userType}
                    defaultUser={defaultUser}
                    isAtBottom={nav?.isAtBottom}
                    showNewBadge={nav?.showNewBadge}
                  />
                  <Divider />
                </div>
              ) : null}
            </>
          ))
      );
    }
  }, [isTestDriveUser, sideBarNavOptions, pathname, sideBarConfig, isDemoUser, open, IsContentAIUser, isInternalUser]);

  const getSideBarSelectAtBottomComponent = useCallback(() => {
    if (isTestDriveUser !== undefined) {
      return (
        sideBarNavOptions
          //.filter((nav) => isValid || isMenuLinkVisible(nav.uniqueKey))
          .map((nav) => (
            <>
              {nav?.selectBox && nav?.isAtBottom ? (
                <div className={nav?.selectBox ? 'sidebar-select-box' : ''}>
                  <DropDownMenu
                    id={`layout__menu__side_bar__${nav.key}`}
                    key={nav.key}
                    title={nav.title}
                    name={nav.key}
                    setAnchor={setAnchor}
                    pathname={pathname}
                    icon={nav.icon}
                    subNavs={nav.subNavs}
                    isSection={nav.isSection}
                    sideBarConfig={
                      defaultUser !== USER_CLASSIFICATION.QWA_TOOLS ? sideBarConfig : sidebarConfigAiToolsSpecificUser
                    }
                    isDisabledMenu={isMenuLinkVisible(nav.uniqueKey)}
                    isDemoUser={isDemoUser}
                    isDrawerOpen={open}
                    to={nav.to}
                    toExternal={nav.toExternal}
                    IsPlaneNav={nav.IsPlaneNav}
                    keepDefaultExpanded={IsContentAIUser || isMenuLinkVisible('is-content-enabled-only')}
                    isHelpCenter={nav.key === 'helpcenter'}
                    customerName={
                      customerData
                        ? customerData?.cust_name
                        : defaultUser === USER_CLASSIFICATION.QWA_TOOLS
                        ? USER_CLASSIFICATION.QWA_TOOLS
                        : null
                    }
                    selectBox={nav?.selectBox}
                    filterChange={props?.handleUserToggle}
                    options={props?.options}
                    userType={userType}
                    defaultUser={defaultUser}
                    isAtBottom={nav?.isAtBottom}
                    showNewBadge={nav?.showNewBadge}
                  />
                  <Divider />
                </div>
              ) : null}
            </>
          ))
      );
    }
  }, [isTestDriveUser, sideBarNavOptions, pathname, sideBarConfig, isDemoUser, open, IsContentAIUser, isInternalUser]);

  return (
    <>
      <Drawer
        variant="permanent"
        className={`drawer ${open ? 'open' : 'close'}  ${
          (props.isMobile && open) || (!props.isMobile && open) ? 'sidebar-mobile-open' : ''
        } ${showSubscriberBanner ? (props.isMobile ? '' : 'add-30-pd') : ''}`}
        onMouseEnter={() => {
          if (!props.isMobile && !open) {
            if (hoverTimeout) {
              clearTimeout(hoverTimeout);
              setHoverTimeout(null);
            }
            props.toggleDrawer(true);
          }
        }}
        onMouseLeave={() => {
          // Close sidebar when mouse enters backdrop area (outside sidebar)
          const timeout = setTimeout(() => {
            props.toggleDrawer(false);
          }, 50);
          setHoverTimeout(timeout);
        }}
      >
        {isMobile ? (
          <Backdrop
            open={open}
            onClick={() => props.toggleDrawer(false)}
            onMouseEnter={() => {
              if (!props.isMobile && open) {
                // Close sidebar when mouse enters backdrop area (outside sidebar)
                const timeout = setTimeout(() => {
                  props.toggleDrawer(false);
                }, 50);
                setHoverTimeout(timeout);
              }
            }}
          />
        ) : null}
        <List
          className={`sidebarlistwrapper ${open ? 'sidebar-list-open' : 'sidebar-list-close'}`}
          onMouseLeave={() => {
            if (!props.isMobile && open) {
              // Close sidebar when mouse leaves the sidebar content
              const timeout = setTimeout(() => {
                props.toggleDrawer(false);
              }, 150);
              setHoverTimeout(timeout);
            }
          }}
          onMouseEnter={() => {
            // Cancel any pending close when mouse re-enters sidebar
            if (hoverTimeout) {
              clearTimeout(hoverTimeout);
              setHoverTimeout(null);
            }
          }}
        >
          <div id="layout__div__side_bar_0" className="sidebar-options">
            <Divider />
            {getSideBarComponent()}
          </div>
          <div>
            <Divider className={` ${isProductionEnv ? 'd-none' : ''}`} />
            {userType == USER_CLASSIFICATION.QWA_TOOLS &&
            (props.defaultUser === USER_CLASSIFICATION.QWA_TOOLS ||
              (session?.user?.userType === USER_CLASSIFICATION.CONTENT_AI &&
                ![
                  SUBSCRIPTION_STATUS.SUBSCRIPTION_ACTIVATED,
                  SUBSCRIPTION_STATUS.SUBSCRIPTION_REACTIVATED,
                  SUBSCRIPTION_STATUS.SUBSCRIPTION_RESUMED,
                ].includes(session?.user?.subscription_status as any))) ? (
              <CreditsComponent />
            ) : null}
            {open && getSideBarSelectAtBottomComponent()}
            <div id="layout__div__side_bar_1" className="copyrightwrap">
              © Quattr Inc. {fullYear}
            </div>
          </div>
        </List>
      </Drawer>
      {/* On hover display submenu or menu */}
      <Qpopper setAnchor={setAnchor} anchorEl={anchorData?.ele} placement="right-start" useDelayForClosing>
        <div id="layout__div__side_bar_2" className="popper-container" onMouseLeave={() => setAnchor(null)}>
          <List component="div" disablePadding>
            {hoveredSubnavs?.subNavs
              ? hoveredSubnavs.subNavs.map((item) => (
                  <Link key={item.to} href={item.to || ''} legacyBehavior>
                    <ListItem
                      button
                      className={`list-item ${isSideNavItemActive(item, pathname, personalFolderIds) ? 'active' : ''}`}
                    >
                      <ListItemText primary={item.title} />
                    </ListItem>
                  </Link>
                ))
              : null}
          </List>
        </div>
      </Qpopper>
    </>
  );
}
